import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { SelectOption } from 'projects/component-library/components/input-select/select-option';
import { ModalComponent } from 'projects/component-library/components/modal/modal.component';
import { SharedValidators, validateForm } from 'projects/shared/validators/shared-validators';
import { DeleteUsersRequest, UpdateUserRequest } from 'projects/shared/services/inspection-companies/inspection-company.user.service';
import { DeleteUserConfirmationModalComponent } from '../delete/delete-user-confirmation-modal.component';
import { Observable } from 'rxjs';
import { UsersDeletedResponse } from 'projects/shared/services/inspection-companies/models/users/users-deleted-response';
import { RoleResponse } from 'projects/shared/services/roles/models/role.response';
import { Permission } from 'projects/shared/directives/if-permission.directive';

@Component({
  selector: 'shared-edit-user-modal',
  standalone: false,
  template: `<mac-modal #modal>
      <mac-modal-title>
        {{ 'Portal.EditUserModal.Title' | translate }}
      </mac-modal-title>

      <mac-modal-content>
        <app-alert [errorKeys]="errorKeys" type="error"> </app-alert>
        <app-alert [errorKeys]="warningKeys" type="warning"> </app-alert>
        <form [formGroup]="editUserForm" [id]="formId" nz-form (ngSubmit)="onSubmit()">
          <mac-form-input-text
            [formControl]="emailControl"
            type="email"
            [label]="'Portal.EditUserModal.Form.Email.Label' | translate"
            [placeholder]="'Portal.EditUserModal.Form.Email.Placeholder' | translate">
          </mac-form-input-text>
          <mac-form-input-text
            [formControl]="firstNameControl"
            [label]="'Portal.EditUserModal.Form.FirstName.Label' | translate"
            [placeholder]="'Portal.EditUserModal.Form.FirstName.Placeholder' | translate">
          </mac-form-input-text>

          <mac-form-input-text
            [formControl]="lastNameControl"
            [label]="'Portal.EditUserModal.Form.LastName.Label' | translate"
            [placeholder]="'Portal.EditUserModal.Form.LastName.Placeholder' | translate">
          </mac-form-input-text>

          <app-roles-select-input [roles$]="roles$" [formControl]="rolesControl" moreInfoLink="roles" (error)="onHttpError($event)" />

          <app-culture-select-input [formControl]="cultureControl" (error)="onHttpError($event)"> </app-culture-select-input>
        </form>
      </mac-modal-content>

      <mac-modal-footer>
        <div nz-row style="width: 100%">
          <div nz-col *ngIf="canDeleteUser">
            <mac-modal-delete-button *ifPermission="deleteUserPermission" (click)="onDeleteUser()"></mac-modal-delete-button>
          </div>
          <div nz-col nzFlex="auto">
            <div nz-row style="float: right">
              <mac-modal-cancel-button (cancel)="close()"> </mac-modal-cancel-button>

              <mac-button *ifPermission="updateUserPermission" type="submit" [form]="formId" [isLoading]="isSavingForm" style="padding-left: 16px">
                {{ 'Portal.EditUserModal.Form.SubmitButton.Label' | translate }}
              </mac-button>
            </div>
          </div>
        </div>
      </mac-modal-footer>
    </mac-modal>

    <shared-delete-user-confirmation-modal
      #deleteUserConfirmationModal
      [userEmail]="user.email"
      [deleteUsersAction]="deleteUsersAction"
      (userDeleteModalClosed)="onUserDeleteModalClosed($event)" /> `,
})
export class EditUserModalComponent {
  readonly deleteUserPermission = Permission.DeleteInspectionCompanyUser;
  readonly updateUserPermission = Permission.UpdateInspectionCompanyUsers;

  @Input() user: User;
  @Input() deleteUsersAction: (model: DeleteUsersRequest) => Observable<UsersDeletedResponse>;
  @Input() updateUserAction: (userId: number, model: UpdateUserRequest) => Observable<void>;
  @Input({ required: true }) roles$: Observable<RoleResponse[]>;

  @Output() userUpdated = new EventEmitter<void>();
  @Output() userDeleteModalClosed = new EventEmitter<boolean>();

  @ViewChild('modal') modal: ModalComponent;
  @ViewChild('deleteUserConfirmationModal') deleteUserConfirmationModal: DeleteUserConfirmationModalComponent;

  public editUserForm: FormGroup;
  public emailControl = new FormControl<string | null>(null, [Validators.required, Validators.email, Validators.maxLength(255)]);
  public firstNameControl = new FormControl<string | null>(null, [Validators.required, Validators.maxLength(255)]);
  public lastNameControl = new FormControl<string | null>(null, [Validators.required, Validators.maxLength(255)]);
  public rolesControl = new FormControl<string[] | null>(null, [SharedValidators.minLengthArray(1, 'atLeastOneRole')]);
  public cultureControl = new FormControl<number | null>(null, [Validators.required]);

  public roles: SelectOption[] = [];
  public cultures: SelectOption[] = [];
  public isSavingForm = false;
  public errorKeys: string[] = [];
  public warningKeys: string[] = [];

  constructor(private formBuilder: FormBuilder) {
    this.editUserForm = this.formBuilder.group({
      email: this.emailControl,
      firstName: this.firstNameControl,
      lastName: this.lastNameControl,
      roles: this.rolesControl,
      culture: this.cultureControl,
    });
  }

  public get formId(): string {
    return `update-user-form-${this.user.userId}`;
  }

  public get canDeleteUser(): boolean {
    return !!this.deleteUsersAction;
  }

  public open() {
    this.errorKeys = [];
    this.warningKeys = [];
    this.initializeForm();
    this.modal.openModal();
  }

  public close() {
    this.modal.closeModal();
  }

  public onSubmit() {
    validateForm(this.editUserForm);
    if (this.editUserForm.invalid) {
      return;
    }

    this.setFormState('saving');
    const model = {
      email: this.emailControl.value!,
      firstName: this.firstNameControl.value!,
      lastName: this.lastNameControl.value!,
      roles: this.rolesControl.value!,
      cultureId: this.cultureControl.value!,
    };
    this.updateUserAction(this.user.userId, model).subscribe({
      next: () => {
        this.setFormState('ready');
        this.userUpdated.emit();
      },
      error: (error) => {
        this.setFormState('ready');
        this.errorKeys = error.error.errors;
      },
    });
  }

  public onHttpError(errorKey: string) {
    this.errorKeys = [...this.errorKeys, errorKey];
  }

  private setFormState(state: 'ready' | 'saving') {
    switch (state) {
      case 'ready':
        this.isSavingForm = false;
        break;
      case 'saving':
        this.isSavingForm = true;
        this.editUserForm.disable();
    }
  }

  private initializeForm() {
    this.emailControl.setValue(this.user.email);
    this.firstNameControl.setValue(this.user.firstName);
    this.lastNameControl.setValue(this.user.lastName);
    this.rolesControl.setValue(this.user.roles.map((role) => role.key));
    this.cultureControl.setValue(this.user.cultureId);
    if (this.user != undefined && this.user.isLinkedToAnotherInspectionCompany != undefined && this.user.isLinkedToAnotherInspectionCompany) {
      this.emailControl.disable();
      this.firstNameControl.disable();
      this.lastNameControl.disable();
      this.warningKeys = ['UserLinkedToAnotherInspectionCompany'];
    } else {
      this.emailControl.enable();
      this.firstNameControl.enable();
      this.lastNameControl.enable();
    }
    if (!this.editUserForm) {
      this.editUserForm = this.formBuilder.group({
        email: this.emailControl,
        firstName: this.firstNameControl,
        lastName: this.lastNameControl,
        roles: this.rolesControl,
        culture: this.cultureControl,
      });
    }
  }

  onDeleteUser() {
    this.modal.closeModal();
    this.deleteUserConfirmationModal.openModal();
  }

  onUserDeleteModalClosed(isDeleted: boolean) {
    this.userDeleteModalClosed.emit(isDeleted);
  }
}

export interface User {
  userId: number;
  cultureId: number | null;
  email: string;
  firstName: string | null;
  lastName: string | null;
  isLinkedToAnotherInspectionCompany: boolean | null;
  roles: Role[];
}

export interface Role {
  name: string;
  key: string;
}
